import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "../../../component/commonCard/style.module.css";
import styles from "../AnalyticsDashboard.module.css";
import LeaderboardTable from "../../../component/commonCard/parts/LeaderboardTable";
import { getContentAnalytics } from "../../../api/HttpApis";
import { showToast } from "../../../utils/Toastify/Toastify";
import ExportButtons from "../ExportButtons";
import useUpdateSearchParams from "../../../hooks/useUpdateSearchParams";
import { truncateChars } from "../../../utils/textTruncate";

const SORTS = [
  { key: "starts", label: "Most Viewed" },
  { key: "completion_percent", label: "Highest Completion" },
  { key: "plays_7d", label: "Trending (7d)" },
];

const ContentSection = () => {
  const [searchParams, updateParams] = useUpdateSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const limit = 10;

  // Sort and page live in the URL (like every other filter here, see
  // FilterBar.js) so browser Back restores the exact list state instead of
  // resetting to page 1 / default sort after a drill-down visit.
  const sort = searchParams.get("sort") || "starts";
  const page = Number(searchParams.get("page")) || 1;
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const platform = searchParams.get("platform") || "";
  const contentType = searchParams.get("contentType") || "";

  // A filter change (from/to/platform/contentType) should reset to page 1,
  // but resetting and fetching used to be two separate effects: the fetch
  // effect fired once with the old, stale page number and then again a
  // render later once the reset effect landed page=1 in the URL. Doing the
  // reset-detection and the fetch in the same effect skips that wasted
  // first fetch -- when filters changed away from page 1, we push page=1
  // and wait for the resulting re-render instead of fetching immediately.
  const filtersKey = `${from}|${to}|${platform}|${contentType}`;
  const prevFiltersKeyRef = useRef(filtersKey);

  useEffect(() => {
    const filtersChanged = prevFiltersKeyRef.current !== filtersKey;
    prevFiltersKeyRef.current = filtersKey;

    if (filtersChanged && page !== 1) {
      updateParams({ page: 1 });
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await getContentAnalytics({ sort, page, limit, platform, contentType, from, to });
        if (!cancelled) setData(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) showToast(err?.response?.data?.message || "Failed to load content analytics", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, page, filtersKey]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const drilldownParams = new URLSearchParams(searchParams);

  const toExportRow = (item) => ({
    Slug: item.slug,
    Title: item.title || item.slug,
    Views: item.starts,
    Completions: item.completes,
    "Completion %": item.completion_percent,
    "Plays (7d)": item.plays_7d,
  });

  // The table only ever holds one page (`limit` rows) -- export needs every
  // title matching the current sort/filters, so re-query with a limit large
  // enough to cover them all instead of exporting just what's on screen.
  const fetchAllExportRows = async () => {
    if (!total) return [];
    const res = await getContentAnalytics({ sort, page: 1, limit: total, platform, contentType, from, to });
    const allItems = res?.data?.data?.items || [];
    return allItems.map(toExportRow);
  };

  return (
    <>
      <div className={styles.filterGroup}>
        {SORTS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={sort === s.key ? styles.presetActive : styles.preset}
            onClick={() => updateParams({ sort: s.key, page: 1 })}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styled.loading}>Loading content analytics…</div>
      ) : (
        <>
          <ExportButtons rows={items.map(toExportRow)} fetchRows={fetchAllExportRows} filename="content-analytics" />
          <LeaderboardTable
            title="Content Performance"
            rows={items}
            emptyHint="No content plays recorded for the selected filters yet."
            columns={[
              {
                key: "title",
                label: "Title",
                render: (v, r) => {
                  const label = v || r.slug;
                  return (
                    <Link title={label} to={`content/${encodeURIComponent(r.slug)}?${drilldownParams.toString()}`}>
                      {truncateChars(label)}
                    </Link>
                  );
                },
              },
              { key: "starts", label: "Views", align: "right" },
              { key: "completes", label: "Completions", align: "right" },
              { key: "completion_percent", label: "Completion %", align: "right", render: (v) => `${v}%` },
              { key: "plays_7d", label: "Plays (7d)", align: "right" },
            ]}
          />
          <div className={styles.pagination}>
            <button type="button" disabled={page <= 1} onClick={() => updateParams({ page: page - 1 })} className={styles.preset}>
              Previous
            </button>
            <span>Page {page} · {total} titles</span>
            <button type="button" disabled={page * limit >= total} onClick={() => updateParams({ page: page + 1 })} className={styles.preset}>
              Next
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default ContentSection;
